// frontend/src/components/public/blocks/ProgrammeInitiativesBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent, CardMedia, Chip, Button, Avatar } from '@mui/material';
import { CheckCircle as CheckIcon, TrendingUp as TrendingUpIcon, FormatQuote as QuoteIcon } from '@mui/icons-material';

interface ProgrammeInitiativesBlockProps {
  data: {
    sectionTitle: string;
    sectionSubtitle: string;
    titleColor: 'primary' | 'accent';
    subtitleColor: 'primary' | 'accent';
    programs: Array<{
      id: string;
      image: string;
      name: string;
      status: 'active' | 'past' | 'seasonal' | 'on-demand';
      year: number;
      description: string;
      keyDetails: string[];
      categories: string[];
      link: string;
      linkText: string;
      testimonial: {
        enabled: boolean;
        quote: string;
        partnerName: string;
        partnerRole: string;
        partnerImage: string;
      };
      fundingAvailable: boolean;
    }>;
    layout: 'cards' | 'timeline' | 'list';
    showStatusBadges: boolean;
    showFundingBadges: boolean;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const ProgrammeInitiativesBlock: React.FC<ProgrammeInitiativesBlockProps> = ({ data, theme }) => {
  const {
    sectionTitle,
    sectionSubtitle,
    titleColor = 'primary',
    subtitleColor = 'primary',
    programs = [],
    layout = 'cards',
    showStatusBadges = true,
    showFundingBadges = true
  } = data;

  const accentColor = theme?.accentColor || '#FF0000';
  const primaryColor = theme?.primaryColor || '#000000';

  const getTitleColor = (colorChoice: 'primary' | 'accent') => {
    return colorChoice === 'primary' ? primaryColor : accentColor;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#4caf50';
      case 'past':
        return '#9e9e9e';
      case 'seasonal':
        return '#ff9800';
      case 'on-demand':
        return '#2196f3';
      default:
        return accentColor;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'past':
        return 'Past';
      case 'seasonal':
        return 'Seasonal';
      case 'on-demand':
        return 'On-Demand';
      default:
        return status;
    }
  };

  if (programs.length === 0) {
    return null;
  }

  // Sort programs by year for timeline, otherwise keep as-is (manual order)
  const sortedPrograms = layout === 'timeline'
    ? [...programs].sort((a, b) => b.year - a.year)
    : programs;

  // ============================================================================
  // CARDS LAYOUT
  // ============================================================================
  if (layout === 'cards') {
    return (
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1200px',
          mx: 'auto'
        }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: getTitleColor(titleColor),
              mb: 2
            }}
          >
            {sectionTitle}
          </Typography>
          {sectionSubtitle && (
            <Box
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: getTitleColor(subtitleColor),
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8,
                '& p': { margin: 0 },
                '& strong': { fontWeight: 600 }
              }}
              dangerouslySetInnerHTML={{ __html: sectionSubtitle }}
            />
          )}
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={4}>
          {sortedPrograms.map((program) => (
            <Grid item xs={12} md={6} key={program.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${accentColor}22`,
                    borderColor: accentColor
                  }
                }}
              >
                {/* Program Image */}
                {program.image && (
                  <CardMedia
                    component="img"
                    height="240"
                    image={program.image}
                    alt={program.name}
                    sx={{ objectFit: 'cover' }}
                  />
                )}

                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Badges Row */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {showStatusBadges && (
                      <Chip
                        label={getStatusLabel(program.status)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(program.status),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    )}
                    {showFundingBadges && program.fundingAvailable && (
                      <Chip
                        label="Funding Available"
                        size="medium"
                        sx={{
                          bgcolor: accentColor,
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    )}
                    {program.categories.map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        size="medium"
                        variant="outlined"
                        sx={{
                          borderColor: accentColor,
                          color: accentColor,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>

                  {/* Program Name & Year */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: '3rem',
                      fontWeight: 700,
                      color: primaryColor,
                      mb: 0.5
                    }}
                  >
                    {program.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '2rem',
                      color: accentColor,
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    {program.year}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '1rem',
                      lineHeight: 1.7,
                      color: '#ffffffff',
                      mb: 3
                    }}
                  >
                    {program.description}
                  </Typography>

                  {/* Key Details */}
                  {program.keyDetails.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      {program.keyDetails.map((detail, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1
                          }}
                        >
                          <CheckIcon
                            sx={{
                              fontSize: '1.25rem',
                              color: accentColor,
                              mt: 0.25
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.95rem',
                              color: '#f8f898ff',
                              lineHeight: 1.6
                            }}
                          >
                            {detail}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Testimonial */}
                  {program.testimonial.enabled && program.testimonial.quote && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: '#f9f9f9',
                        borderLeft: `4px solid ${accentColor}`,
                        borderRadius: 1,
                        mb: 3
                      }}
                    >
                      <QuoteIcon
                        sx={{
                          fontSize: '2rem',
                          color: accentColor,
                          opacity: 0.4,
                          mb: 1
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                          fontStyle: 'italic',
                          color: '#333333',
                          mb: 2
                        }}
                      >
                        "{program.testimonial.quote}"
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {program.testimonial.partnerImage && (
                          <Avatar
                            src={program.testimonial.partnerImage}
                            sx={{ width: 48, height: 48 }}
                          />
                        )}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: primaryColor,
                              fontSize: '0.9rem'
                            }}
                          >
                            {program.testimonial.partnerName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#666666',
                              fontSize: '0.8rem'
                            }}
                          >
                            {program.testimonial.partnerRole}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Link Button */}
                  {program.link && (
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        href={program.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        fullWidth
                        sx={{
                          bgcolor: accentColor,
                          color: '#ffffff',
                          py: 1.5,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: accentColor,
                            opacity: 0.9,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 20px ${accentColor}44`
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {program.linkText || 'Learn More'}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ============================================================================
  // TIMELINE LAYOUT
  // ============================================================================
  if (layout === 'timeline') {
    return (
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 3, md: 6 },
          maxWidth: '1000px',
          mx: 'auto'
        }}
      >
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: getTitleColor(titleColor),
              mb: 2
            }}
          >
            {sectionTitle}
          </Typography>
          {sectionSubtitle && (
            <Box
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                color: getTitleColor(subtitleColor),
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8,
                '& p': { margin: 0 },
                '& strong': { fontWeight: 600 }
              }}
              dangerouslySetInnerHTML={{ __html: sectionSubtitle }}
            />
          )}
        </Box>

        {/* Timeline */}
        <Box sx={{ position: 'relative' }}>
          {/* Vertical Line */}
          <Box
            sx={{
              position: 'absolute',
              left: { xs: '20px', md: '50%' },
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: accentColor,
              opacity: 0.3,
              transform: { md: 'translateX(-1px)' }
            }}
          />

          {/* Timeline Items */}
          {sortedPrograms.map((program, index) => (
            <Box
              key={program.id}
              sx={{
                position: 'relative',
                mb: 6,
                pl: { xs: '60px', md: 0 }
              }}
            >
              {/* Year Circle */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: '8px', md: '50%' },
                  top: 0,
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  bgcolor: accentColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: { md: 'translateX(-50%)' },
                  zIndex: 2,
                  boxShadow: `0 0 0 4px #ffffff`
                }}
              >
                <Typography
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.75rem'
                  }}
                >
                  {String(program.year).slice(-2)}
                </Typography>
              </Box>

              {/* Content Card */}
              <Box
                sx={{
                  ml: { md: index % 2 === 0 ? 0 : 'calc(50% + 40px)' },
                  mr: { md: index % 2 === 0 ? 'calc(50% + 40px)' : 0 },
                  textAlign: { md: index % 2 === 0 ? 'right' : 'left' }
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 32px ${accentColor}22`,
                      borderColor: accentColor
                    }
                  }}
                >
                  {program.image && (
                    <CardMedia
                      component="img"
                      height="200"
                      image={program.image}
                      alt={program.name}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Badges */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2,
                        justifyContent: { md: index % 2 === 0 ? 'flex-end' : 'flex-start' }
                      }}
                    >
                      {showStatusBadges && (
                        <Chip
                          label={getStatusLabel(program.status)}
                          size="small"
                          sx={{
                            bgcolor: getStatusColor(program.status),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      )}
                      {showFundingBadges && program.fundingAvailable && (
                        <Chip
                          label="Funding Available"
                          size="small"
                          sx={{
                            bgcolor: accentColor,
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>

                    {/* Program Name & Year */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: primaryColor,
                        mb: 1
                      }}
                    >
                      {program.name}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: accentColor,
                        fontWeight: 600,
                        mb: 2
                      }}
                    >
                      {program.year}
                    </Typography>

                    {/* Categories */}
                    {program.categories.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          mb: 2,
                          justifyContent: { md: index % 2 === 0 ? 'flex-end' : 'flex-start' }
                        }}
                      >
                        {program.categories.map((category) => (
                          <Chip
                            key={category}
                            label={category}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: accentColor,
                              color: accentColor,
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Description */}
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        color: '#555555',
                        mb: 2
                      }}
                    >
                      {program.description}
                    </Typography>

                    {/* Key Details */}
                    {program.keyDetails.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        {program.keyDetails.map((detail, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              mb: 0.75,
                              flexDirection: { md: index % 2 === 0 ? 'row-reverse' : 'row' }
                            }}
                          >
                            <CheckIcon
                              sx={{
                                fontSize: '1.1rem',
                                color: accentColor,
                                mt: 0.25,
                                flexShrink: 0
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.875rem',
                                color: '#333333',
                                lineHeight: 1.6
                              }}
                            >
                              {detail}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Testimonial */}
                    {program.testimonial.enabled && program.testimonial.quote && (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: '#f9f9f9',
                          borderLeft: `3px solid ${accentColor}`,
                          borderRadius: 1,
                          mb: 2
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                            color: '#333333',
                            mb: 1.5
                          }}
                        >
                          "{program.testimonial.quote}"
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexDirection: { md: index % 2 === 0 ? 'row-reverse' : 'row' }
                          }}
                        >
                          {program.testimonial.partnerImage && (
                            <Avatar
                              src={program.testimonial.partnerImage}
                              sx={{ width: 40, height: 40 }}
                            />
                          )}
                          <Box sx={{ textAlign: { md: index % 2 === 0 ? 'right' : 'left' } }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: primaryColor,
                                fontSize: '0.85rem'
                              }}
                            >
                              {program.testimonial.partnerName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#666666',
                                fontSize: '0.75rem'
                              }}
                            >
                              {program.testimonial.partnerRole}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Link Button */}
                    {program.link && (
                      <Button
                        href={program.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: accentColor,
                          color: accentColor,
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: accentColor,
                            bgcolor: accentColor,
                            color: '#ffffff'
                          }
                        }}
                      >
                        {program.linkText || 'Learn More'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // ============================================================================
  // LIST LAYOUT
  // ============================================================================
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 3, md: 6 },
        maxWidth: '1000px',
        mx: 'auto'
      }}
    >
      {/* Section Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            fontWeight: 700,
            color: getTitleColor(titleColor),
            mb: 2
          }}
        >
          {sectionTitle}
        </Typography>
        {sectionSubtitle && (
          <Box
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: getTitleColor(subtitleColor),
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8,
              '& p': { margin: 0 },
              '& strong': { fontWeight: 600 }
            }}
            dangerouslySetInnerHTML={{ __html: sectionSubtitle }}
          />
        )}
      </Box>

      {/* List Items */}
      {sortedPrograms.map((program, index) => (
        <Card
          key={program.id}
          elevation={0}
          sx={{
            mb: 4,
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${accentColor}22`,
              borderColor: accentColor
            }
          }}
        >
          <Grid container>
            {/* Image Column */}
            {program.image && (
              <Grid item xs={12} md={4}>
                <CardMedia
                  component="img"
                  height="100%"
                  image={program.image}
                  alt={program.name}
                  sx={{
                    objectFit: 'cover',
                    minHeight: { xs: '200px', md: '100%' }
                  }}
                />
              </Grid>
            )}

            {/* Content Column */}
            <Grid item xs={12} md={program.image ? 8 : 12}>
              <CardContent sx={{ p: 4 }}>
                {/* Badges Row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {showStatusBadges && (
                    <Chip
                      label={getStatusLabel(program.status)}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(program.status),
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                  {showFundingBadges && program.fundingAvailable && (
                    <Chip
                      label="Funding Available"
                      size="small"
                      sx={{
                        bgcolor: accentColor,
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                  {program.categories.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: accentColor,
                        color: accentColor,
                        fontWeight: 500
                      }}
                    />
                  ))}
                </Box>

                {/* Program Name & Year */}
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    color: primaryColor,
                    mb: 0.5
                  }}
                >
                  {program.name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: accentColor,
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  {program.year}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.05rem',
                    lineHeight: 1.8,
                    color: '#555555',
                    mb: 3
                  }}
                >
                  {program.description}
                </Typography>

                {/* Key Details */}
                {program.keyDetails.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={2}>
                      {program.keyDetails.map((detail, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <CheckIcon
                              sx={{
                                fontSize: '1.25rem',
                                color: accentColor,
                                mt: 0.25
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.95rem',
                                color: '#333333',
                                lineHeight: 1.6
                              }}
                            >
                              {detail}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Testimonial */}
                {program.testimonial.enabled && program.testimonial.quote && (
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: '#f9f9f9',
                      borderLeft: `4px solid ${accentColor}`,
                      borderRadius: 1,
                      mb: 3
                    }}
                  >
                    <QuoteIcon
                      sx={{
                        fontSize: '2rem',
                        color: accentColor,
                        opacity: 0.4,
                        mb: 1
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.7,
                        fontStyle: 'italic',
                        color: '#333333',
                        mb: 2
                      }}
                    >
                      "{program.testimonial.quote}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {program.testimonial.partnerImage && (
                        <Avatar
                          src={program.testimonial.partnerImage}
                          sx={{ width: 56, height: 56 }}
                        />
                      )}
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: primaryColor,
                            fontSize: '0.95rem'
                          }}
                        >
                          {program.testimonial.partnerName}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#666666',
                            fontSize: '0.85rem'
                          }}
                        >
                          {program.testimonial.partnerRole}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Link Button */}
                {program.link && (
                  <Button
                    href={program.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    sx={{
                      bgcolor: accentColor,
                      color: '#ffffff',
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: accentColor,
                        opacity: 0.9,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 20px ${accentColor}44`
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {program.linkText || 'Learn More'}
                  </Button>
                )}
              </CardContent>
            </Grid>
          </Grid>
        </Card>
      ))}
    </Box>
  );
};

export default ProgrammeInitiativesBlock;